/**
 * QuoteDiagnosticsPanel — pricing engine / corridor debug fields (collapsed by default).
 */

import React, { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { getQuoteInternalSnapshot } from "../services/quoteOperationalStatus.js";
import QuotePricingExplanationTree from "./QuotePricingExplanationTree.jsx";
import VolumeMetricHelpBubble from "./VolumeMetricHelpBubble.jsx";

const Panel = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: 12,
  overflow: "hidden",
  backgroundColor: theme.palette.background.paper,
}));

const Field = ({ label, value, labelHelpMetric }) => (
  <Box>
    <Stack direction="row" alignItems="center" spacing={0}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {labelHelpMetric ? <VolumeMetricHelpBubble metric={labelHelpMetric} /> : null}
    </Stack>
    <Typography variant="body2" sx={{ fontWeight: 600 }}>
      {value == null || value === "" ? "—" : String(value)}
    </Typography>
  </Box>
);

const QuoteDiagnosticsPanel = ({ quote }) => {
  const [expanded, setExpanded] = useState(false);
  const snap = getQuoteInternalSnapshot(quote);
  const explanation = quote?.automatedPreparation?.pricingExplanation || null;

  if (!quote) return null;

  return (
    <Panel data-testid="quote-diagnostics-panel">
      <Accordion
        expanded={expanded}
        onChange={(_, next) => setExpanded(next)}
        disableGutters
        elevation={0}
        sx={{ background: "transparent" }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Advanced diagnostics
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Pricing engine metadata — for support and debugging
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Grid container spacing={2} mb={2}>
            <Grid item xs={6}>
              <Field label="Pricing engine" value={snap.engineLabel} />
            </Grid>
            <Grid item xs={6}>
              <Field label="Corridor" value={snap.corridor} />
            </Grid>
            <Grid item xs={6}>
              <Field
                label="Billable volume"
                labelHelpMetric="billable"
                value={
                  snap.billableVolumeM3 != null
                    ? `${Number(snap.billableVolumeM3).toFixed(2)} m³`
                    : "—"
                }
              />
            </Grid>
            <Grid item xs={6}>
              <Field label="Truck" value={snap.truckSize || "—"} />
            </Grid>
          </Grid>
          {explanation ? <QuotePricingExplanationTree explanation={explanation} /> : null}
        </AccordionDetails>
      </Accordion>
    </Panel>
  );
};

export default QuoteDiagnosticsPanel;
